import type { SportsPillTone } from '@/components/features/SportsMarkets/SportsOutcomePill';
import type { BuyTargetDto, MarketOptionDto } from '@/lib/models';

/**
 * Map an outcome side to its pill colour scheme: away/over → purple, home/under →
 * blue, draw → grey. Unknown/null sides fall back to purple. Matches the Crown
 * Prediction sports design (Figma 5138 / 5379).
 */
export function sideTone(side: string | null | undefined): SportsPillTone {
  switch (side) {
    case 'HOME':
    case 'UNDER':
      return 'blue';
    case 'DRAW':
      return 'grey';
    default:
      return 'purple';
  }
}

/** The token CSS variable backing each fallback tone (for solid fills / borders). */
const TONE_VAR: Record<SportsPillTone, string> = {
  purple: 'var(--sports-purple)',
  blue: 'var(--sports-blue)',
  grey: 'var(--sports-draw)',
};

/**
 * The effective moneyline answer colour (ARC-80 §6): the Plaee-provided `color`
 * when present, else the Figma default for the outcome's side. A CSS colour string
 * suitable for an inline `style` (Plaee colour is genuinely per-team dynamic data).
 */
export function sportsColor(color: string | null | undefined, side: string | null | undefined): string {
  return color ?? TONE_VAR[sideTone(side)];
}

/** A sports outcome (moneyline answer or spread/total leg) — the buy-relevant fields. */
interface SportsBuyOutcome {
  outcomeId: string;
  label: string;
  chancePercent: number | null;
  yes: BuyTargetDto;
  no: BuyTargetDto;
}

/**
 * Adapt a sports outcome + its market into the `MarketOptionDto` the shared buy-in
 * popup consumes — so tapping any moneyline/spread/total button opens the same flow
 * (ARC-80 §7). Used by the lobby card and the inside-event blocks.
 */
export function sportsBuyOption(
  o: SportsBuyOutcome,
  market: { marketId: string; externalMarketId: string },
): MarketOptionDto {
  return {
    id: o.outcomeId,
    label: o.label,
    chancePercent: o.chancePercent,
    marketId: market.marketId,
    externalMarketId: market.externalMarketId,
    yes: o.yes,
    no: o.no,
  };
}
